import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Divider,
  Alert,
  Stack,
  Button
} from '@mui/material';
import {
  Help as HelpIcon,
  Email,
  BugReport,
  AccessTime,
  SupportAgent,
  Code,
  Info,
  Download
} from '@mui/icons-material';
import { SOFTWARE_DOWNLOAD_URL } from '../constants/software';

export default function Help() {
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <HelpIcon color="primary" sx={{ fontSize: 40 }} />
          Help & Support
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          Welcome to TimeSentinel! We're here to help you make the most of your time tracking experience. 
          Whether you have questions, need assistance, or want to share feedback, we've got you covered.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Support Contact Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Email color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h5" component="h2">
                  Contact Support
                </Typography>
              </Stack>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="body1" paragraph>
                Need help? Our support team is ready to assist you with any questions or issues you may have.
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  <strong>Email us at:</strong>
                </Typography>
                <Typography variant="body1" component="a" href="mailto:support@time-sentinel.com" sx={{ 
                  color: 'primary.main', 
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}>
                  support@time-sentinel.com
                </Typography>
              </Alert>
              <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <AccessTime color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Response Time:</strong> We aim to respond to all inquiries within one business day.
                  </Typography>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Bug Reports & Feedback Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <BugReport color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h5" component="h2">
                  Report Issues
                </Typography>
              </Stack>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="body1" paragraph>
                Found a bug or have a suggestion? We value your feedback and want to make TimeSentinel better for everyone.
              </Typography>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Info />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                      <strong>Preferred Method:</strong>
                    </Typography>
                    <Typography variant="body2">
                      The feedback function within the TimeSentinel app is the preferred way to report errors, bugs, and share your suggestions. 
                      This helps us track and prioritize issues more effectively.
                    </Typography>
                  </Box>
                </Stack>
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                When reporting issues, please include as much detail as possible, such as what you were doing when the issue occurred, 
                any error messages you saw, and steps to reproduce the problem.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Release & Development Channels Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Code color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h5" component="h2">
                  Release & Development Channels
                </Typography>
              </Stack>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="body1" paragraph>
                TimeSentinel offers two update channels to suit different needs and preferences.
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Release Channel (Recommended)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    The release channel provides stable, thoroughly tested versions of TimeSentinel. 
                    We recommend all users stay on this channel for the most reliable experience.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Development Channel
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    The development channel is available through the menus in the application. 
                    This channel gives you the option to update the software to the latest development version, 
                    allowing you to test the latest features, fixes, and improvements before they're released to everyone.
                  </Typography>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> Development versions may contain experimental features and could be less stable than release versions. 
                      Use at your own discretion.
                    </Typography>
                  </Alert>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Download Software Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Download color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h5" component="h2">
                  Download Software
                </Typography>
              </Stack>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="body1" paragraph>
                Download the latest version of the TimeSentinel desktop application for Windows.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<Download />}
                onClick={() => window.open(SOFTWARE_DOWNLOAD_URL, '_blank')}
                sx={{ mt: 2, mb: 2 }}
                fullWidth
              >
                Download TimeSentinel
              </Button>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> When downloading and running the installer, Windows may show a SmartScreen warning. 
                  This is normal and safe. The software is properly code-signed and certified. 
                  Click "More info" and then "Run anyway" to proceed with installation.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Support Team Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <SupportAgent color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h5" component="h2">
                  Our Commitment
                </Typography>
              </Stack>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="body1" paragraph>
                At TimeSentinel, we're committed to providing you with the best possible experience. 
                Your success is our success, and we're here to support you every step of the way.
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText', mt: 2 }}>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  "We believe in building tools that empower teams to work more efficiently. 
                  Your feedback helps us improve, and we're grateful for every suggestion and report."
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>
                  — The TimeSentinel Team
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Footer Note */}
      <Box sx={{ mt: 4, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Thank you for using TimeSentinel. We appreciate your business and look forward to helping you succeed!
        </Typography>
      </Box>
    </Box>
  );
}

